import { TelegramAuthProvider, useAuth } from './contexts';
import { SimpleRouter, Navigation, Breadcrumbs, getBreadcrumbs, ExerciseSelectorDemo, ProgramManager, WorkoutManager, CommentDemo, WorkoutHistoryDemo, AnalyticsDemo, LocalizationDemo, ResponsiveTestSuite, WorkflowTestSuite, IntegrationValidator, SimpleAuth } from './components';
import type { Route } from './components/SimpleRouter';
import './index.css';

function AppContent() {
  const { user, telegramUser, isAuthenticated, isLoading, error, refreshSession } = useAuth();

  if (isLoading) {
    return (
      <div className="tg-webapp min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tg-button mx-auto mb-4"></div>
          <p className="text-tg-secondary">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tg-webapp min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold mb-2">Ошибка аутентификации</h1>
          <p className="text-tg-secondary mb-4">{error}</p>
          <div className="space-y-2">
            <button 
              onClick={refreshSession} 
              className="btn-primary w-full"
            >
              Повторить попытку
            </button>
            <button 
              onClick={() => window.location.reload()} 
              className="btn-secondary w-full"
            >
              Перезагрузить приложение
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show SimpleAuth if not in dev mode and not authenticated
  if (import.meta.env.VITE_DEV_MODE !== 'true' && !isAuthenticated) {
    return (
      <SimpleAuth 
        onAuthSuccess={(userId) => {
          console.log('Auth success, user ID:', userId);
          // Force refresh to re-check authentication
          window.location.reload();
        }} 
      />
    );
  }

  const renderHomePage = (navigate: (route: Route) => void) => (
    <div className="container mx-auto px-4 py-6 pb-20">
      <header className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">🏋️‍♂️ Дневник тренировок</h1>
        <p className="text-tg-secondary">Ваш персональный помощник в зале</p>
      </header>

      {isAuthenticated && user && telegramUser ? (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-2">Добро пожаловать!</h2>
          <p className="text-tg-secondary">
            Привет, {telegramUser.first_name}! 👋
          </p>
          <div className="text-sm text-tg-secondary mt-2 space-y-1">
            <p>Telegram ID: {telegramUser.id}</p>
            <p>User ID: {user.id}</p>
            <p>Язык: {user.language_code || 'ru'}</p>
          </div>
        </div>
      ) : (
        <div className="card mb-6">
          <h2 className="text-lg font-semibold mb-2">Режим разработки</h2>
          <p className="text-tg-secondary">
            Приложение запущено вне Telegram WebApp
          </p>
        </div>
      )}

      <div className="space-y-4">
        <div className="card">
          <h3 className="font-semibold mb-2">📋 Мои программы</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Создавайте и управляйте тренировочными программами
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('programs')}
          >
            Управление программами
          </button>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-2">💪 Начать тренировку</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Логируйте упражнения и отслеживайте прогресс
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('workout')}
          >
            Начать тренировку
          </button>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-2">📊 Аналитика прогресса</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Отслеживайте свой прогресс и достижения
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('analytics')}
          >
            Открыть аналитику
          </button>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-2">📈 История тренировок</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Просматривайте прошлые тренировки
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('history')}
          >
            Открыть историю
          </button>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-2">🎯 Каталог упражнений</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Изучайте базу упражнений с описаниями
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('exercises')}
          >
            Открыть каталог
          </button>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-2">🌍 Локализация</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Тестирование русской локализации
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('localization')}
          >
            Демо локализации
          </button>
        </div>

        <div className="card">
          <h3 className="font-semibold mb-2">💬 Система комментариев</h3>
          <p className="text-tg-secondary text-sm mb-3">
            Тестирование комментариев и быстрых фраз
          </p>
          <button 
            className="btn-primary w-full"
            onClick={() => navigate('comments')}
          >
            Демо комментариев
          </button>
        </div>

        {/* Показываем тестовые компоненты только в dev режиме */}
        {import.meta.env.VITE_DEV_MODE === 'true' && (
          <div className="card">
            <h3 className="font-semibold mb-2">🧪 Тестирование системы</h3>
            <p className="text-tg-secondary text-sm mb-3">
              Проверка совместимости и адаптивности
            </p>
            <div className="space-y-2">
              <button 
                className="btn-primary w-full"
                onClick={() => navigate('responsive-test')}
              >
                Тест адаптивности
              </button>
              <button 
                className="btn-secondary w-full"
                onClick={() => navigate('workflow-test')}
              >
                Тест рабочих процессов
              </button>
              <button 
                className="btn-secondary w-full"
                onClick={() => navigate('integration-test')}
              >
                Интеграционное тестирование
              </button>
            </div>
          </div>
        )}
      </div>

      <footer className="text-center mt-8 pt-6 border-t border-tg-secondary">
        <p className="text-tg-secondary text-sm">
          Версия 0.1.0 • Создано с ❤️ для спортсменов
        </p>
      </footer>
    </div>
  );

  const renderExercisesPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('exercises')} onNavigate={navigate} />
      <ExerciseSelectorDemo />
    </div>
  );

  const renderProgramsPage = (navigate: (route: Route) => void) => (
    <div className="container mx-auto px-4 py-6 pb-20">
      <Breadcrumbs items={getBreadcrumbs('programs')} onNavigate={navigate} />
      <ProgramManager />
    </div>
  );

  const renderWorkoutPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('workout')} onNavigate={navigate} />
      <WorkoutManager onBack={() => navigate('home')} />
    </div>
  );

  const renderCommentsPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('comments')} onNavigate={navigate} />
      <CommentDemo />
    </div>
  );

  const renderHistoryPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('history')} onNavigate={navigate} />
      <WorkoutHistoryDemo />
    </div>
  );

  const renderAnalyticsPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('analytics')} onNavigate={navigate} />
      <AnalyticsDemo />
    </div>
  );

  const renderLocalizationPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('localization')} onNavigate={navigate} />
      <LocalizationDemo />
    </div>
  );

  const renderResponsiveTestPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('responsive-test')} onNavigate={navigate} />
      <ResponsiveTestSuite />
    </div>
  );

  const renderWorkflowTestPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('workflow-test')} onNavigate={navigate} />
      <WorkflowTestSuite />
    </div>
  );

  const renderIntegrationTestPage = (navigate: (route: Route) => void) => (
    <div className="pb-20">
      <Breadcrumbs items={getBreadcrumbs('integration-test')} onNavigate={navigate} />
      <IntegrationValidator />
    </div>
  );

  return (
    <div className="tg-webapp min-h-screen bg-gray-50">
      <SimpleRouter>
        {(currentRoute: Route, navigate) => (
          <>
            {/* Main Content */}
            {currentRoute === 'home' && renderHomePage(navigate)}
            {currentRoute === 'exercises' && renderExercisesPage(navigate)}
            {currentRoute === 'programs' && renderProgramsPage(navigate)}
            {currentRoute === 'workout' && renderWorkoutPage(navigate)}
            {currentRoute === 'comments' && renderCommentsPage(navigate)}
            {currentRoute === 'history' && renderHistoryPage(navigate)}
            {currentRoute === 'analytics' && renderAnalyticsPage(navigate)}
            {currentRoute === 'localization' && renderLocalizationPage(navigate)}
            {currentRoute === 'responsive-test' && renderResponsiveTestPage(navigate)}
            {currentRoute === 'workflow-test' && renderWorkflowTestPage(navigate)}
            {currentRoute === 'integration-test' && renderIntegrationTestPage(navigate)}

            {/* Navigation */}
            <Navigation currentRoute={currentRoute} onNavigate={navigate} />
          </>
        )}
      </SimpleRouter>
    </div>
  );
}

// Main App component with provider
function App() {
  return (
    <TelegramAuthProvider>
      <AppContent />
    </TelegramAuthProvider>
  );
}

export default App;